/**
 * @fileoverview Type definitions for Cart System
 * Provides TypeScript-style JSDoc types for better IDE support
 */

/**
 * @typedef {Object} CartItem
 * @property {number} id - Line item ID
 * @property {number} variant_id - Product variant ID
 * @property {number} product_id - Product ID
 * @property {string} product_title - Product title
 * @property {string} variant_title - Variant title
 * @property {string} vendor - Product vendor
 * @property {number} quantity - Item quantity
 * @property {number} price - Item price in cents
 * @property {number} final_price - Final price after discounts
 * @property {number} line_price - Total line price
 * @property {number} final_line_price - Final line price after discounts
 * @property {string} image - Item image URL
 * @property {string} url - Product URL
 * @property {Object} properties - Custom properties
 * @property {string[]} options_with_values - Variant options
 * @property {boolean} requires_shipping - Requires shipping
 * @property {number} grams - Item weight in grams
 */

/**
 * @typedef {Object} Cart
 * @property {string} token - Cart token
 * @property {string} note - Cart note
 * @property {Object} attributes - Cart attributes
 * @property {number} total_price - Total price in cents
 * @property {number} total_weight - Total weight in grams
 * @property {number} item_count - Total item count
 * @property {CartItem[]} items - Cart items
 * @property {boolean} requires_shipping - Cart requires shipping
 * @property {string} currency - Cart currency
 * @property {number} items_subtotal_price - Subtotal price in cents
 * @property {number} total_discount - Total discount in cents
 */

/**
 * @typedef {Object} CartEventDetail
 * @property {Cart} cart - Cart object
 * @property {CartItem} [item] - Cart item (for add/update events)
 * @property {string} [error] - Error message (for error events)
 * @property {string} [action] - Action type (for error events)
 * @property {number} [variantId] - Variant ID
 * @property {number} [quantity] - Quantity
 * @property {Object} [properties] - Custom properties
 * @property {Object} [updates] - Update object
 */

/**
 * @typedef {Object} CartConfig
 * @property {boolean} autoOpenDrawer - Auto-open drawer on add to cart
 * @property {number} debounceDelay - Debounce delay for quantity updates
 * @property {boolean} enableNotifications - Enable notifications
 * @property {number} notificationDuration - Notification duration in ms
 * @property {boolean} enableLogging - Enable debug logging
 * @property {string} apiEndpoint - API endpoint base URL
 */

/**
 * @typedef {Object} UIElements
 * @property {NodeList} cartCount - Cart count elements
 * @property {NodeList} cartTotal - Cart total elements
 * @property {NodeList} cartBadge - Cart badge elements
 * @property {NodeList} cartState - Cart state elements
 * @property {NodeList} cartItems - Cart items containers
 * @property {NodeList} cartDrawer - Cart drawer elements
 */

/**
 * @typedef {Object} LoaderStatus
 * @property {string[]} loaded - Loaded modules
 * @property {string[]} loading - Currently loading modules
 * @property {string[]} failed - Failed to load modules
 */

/**
 * @typedef {'success'|'error'|'warning'|'info'} NotificationType
 */

/**
 * @typedef {Object} NotificationOptions
 * @property {NotificationType} type - Notification type
 * @property {number} duration - Duration in milliseconds
 * @property {boolean} closeable - Can be closed by user
 * @property {string} position - Position on screen
 */

/**
 * @typedef {Object} ModuleOptions
 * @property {string} name - Module name
 * @property {Function} init - Initialization function
 * @property {string[]} dependencies - Module dependencies
 * @property {number} priority - Loading priority (1-10)
 * @property {boolean} lazy - Should be lazy loaded
 */

/**
 * @typedef {Object} PerformanceMetrics
 * @property {number} loadTime - Module load time
 * @property {number} initTime - Initialization time
 * @property {number} apiResponseTime - API response time
 * @property {number} domUpdateTime - DOM update time
 */

// Export types for JSDoc usage
export const Types = {};

// Runtime type checking utilities
export const TypeCheckers = {
  /**
   * Check if object is a valid Cart
   * @param {any} obj - Object to check
   * @returns {obj is Cart}
   */
  isCart(obj) {
    return obj && 
           typeof obj === 'object' &&
           (typeof obj.token === 'string' || obj.token === null) && // Allow null token
           typeof obj.total_price === 'number' &&
           typeof obj.item_count === 'number' &&
           Array.isArray(obj.items);
  },

  /**
   * Check if object is a valid CartItem
   * @param {any} obj - Object to check
   * @returns {obj is CartItem}
   */
  isCartItem(obj) {
    return obj &&
           typeof obj === 'object' &&
           typeof obj.id === 'number' &&
           typeof obj.variant_id === 'number' &&
           typeof obj.quantity === 'number' &&
           typeof obj.price === 'number';
  },

  /**
   * Check if value is a valid variant ID
   * @param {any} value - Value to check
   * @returns {boolean}
   */
  isValidVariantId(value) {
    return typeof value === 'number' && value > 0;
  },

  /**
   * Check if value is a valid quantity
   * @param {any} value - Value to check
   * @returns {boolean}
   */
  isValidQuantity(value) {
    return typeof value === 'number' && value >= 0 && Number.isInteger(value);
  }
}; 